<template>
  <BaseModal
    card-width="440px"
    no-padding
    no-exit
    @close="handleClose"
  >
    <template #toolbar>
      <div class="relative w-full">
        <div class="px-5 pt-3 pb-5 w-full flex items-center justify-center">
          <!-- Custom Icon Slot -->
          <slot name="icon">
            <!-- Default icon if none provided -->
            <div
              v-if="buttonVariant === 'danger'"
              class="icon-container danger-icon"
            >
              <WarningIcon class="text-white w-full h-full" />
            </div>
          </slot>
        </div>
        <button
          class="absolute top-0 right-0 p-1.5"
          @click="handleClose"
        >
          <TagRemoveIcon stroke="#303546" />
        </button>
      </div>
    </template>

    <template #default>
      <!-- Modal Content -->
      <div class="px-11 py-3 text-center">
        <!-- Title -->
        <BaseText
          type="label"
          size="lg"
          class="text-text-loud mb-3"
        >
          <slot name="title">
            {{ title }}
          </slot>
        </BaseText>

        <!-- Description -->
        <BaseText
          type="body"
          size="sm"
          class="text-text-normal"
        >
          <slot name="description">
            {{ description }}
          </slot>
        </BaseText>

        <!-- Additional Content Slot -->
        <div
          v-if="$slots['additional-content']"
          class="mt-3"
        >
          <slot name="additional-content" />
        </div>

        <!-- Confirmation Checkbox -->
        <div
          v-if="showCheckbox"
          class="flex items-center justify-center gap-2 mt-4"
        >
          <input
            v-model="userConfirmed"
            type="checkbox"
            class="confirm-checkbox w-4 h-4 focus:ring-transparent cursor-pointer"
          >
          <BaseText
            type="body"
            size="sm"
            class="text-text-muted"
          >
            <slot name="checkbox-label">
              I understand the above information.
            </slot>
          </BaseText>
        </div>
      </div>

      <!-- Modal Actions Footer -->
      <div class="p-5 flex flex-col gap-2">
        <!-- Confirm Button Slot -->
        <slot
          name="confirm-button"
          :handleConfirm="handleConfirm"
          :isFormValid="isFormValid"
          :loading="loading"
        >
          <button
            class="w-full submit-btn text-white rounded-md px-3 py-1.5 transition-colors"
            :disabled="!isFormValid || loading"
            :class="[
              isFormValid ? 'enabled' : 'disabled cursor-default',
              buttonVariant === 'primary' ? 'primary-variant' : 'danger-variant'
            ]"
            @click="handleConfirm"
          >
            <div
              v-if="loading"
              class="w-full flex items-center justify-center"
            >
              <BaseLoadingSpinner small />
            </div>
            <BaseText
              v-else
              type="label"
              size="sm"
            >
              {{ confirmText }}
            </BaseText>
          </button>
        </slot>

        <!-- Cancel Button Slot -->
        <slot
          name="cancel-button"
          :handleClose="handleClose"
        >
          <button
            class="w-full px-3 py-1.5 rounded-md transition-colors hover:bg-neutral-25"
            @click="handleClose"
          >
            <BaseText
              type="label"
              size="sm"
              class="text-text-muted"
            >
              {{ cancelText }}
            </BaseText>
          </button>
        </slot>
      </div>
    </template>
  </BaseModal>
</template>

<script>
import TagRemoveIcon from './Icons/TagRemoveIcon.vue'
import WarningIcon from './Icons/WarningIcon.vue'

export default {
  name: 'BaseConfirmModal',
  components: {
    TagRemoveIcon,
    WarningIcon
  },
  props: {
    // Text props with defaults
    title: {
      type: String,
      default: 'Are you sure?'
    },
    description: {
      type: String,
      default: 'This action cannot be undone.'
    },
    confirmText: {
      type: String,
      default: 'Confirm'
    },
    cancelText: {
      type: String,
      default: 'Cancel'
    },

    // Checkbox
    showCheckbox: {
      type: Boolean,
      default: false
    },

    // Loading state
    loading: {
      type: Boolean,
      default: false
    },

    // Button styling
    buttonVariant: {
      type: String,
      default: 'danger', // 'danger' or 'primary'
      validator: (value) => ['danger', 'primary'].includes(value)
    }
  },
  data () {
    return {
      userConfirmed: false
    }
  },
  computed: {
    isFormValid () {
      return this.userConfirmed || !this.showCheckbox
    }
  },
  methods: {
    handleClose () {
      this.$emit('close')
    },
    handleConfirm () {
      if (this.isFormValid) {
        this.$emit('confirm')
      }
    }
  }
}
</script>

<style scoped>
.confirm-checkbox {
  border-radius: 4px;
}
.confirm-checkbox:checked {
  background-color: #303546;
  color: #303546;
}

/* Icon container styles */
.icon-container {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  padding: 10px;
}

.danger-icon {
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: var(--Secondary-Red-100, #F4003A);
  box-shadow: 0px 1px 2px 0px rgba(124, 0, 31, 0.50), 0px 0px 0px 1px #C80031;
}

/* Button styles */
.submit-btn {
  transition: background-color 150ms ease-in-out, opacity 150ms ease-in-out;
}

/* Danger variant (red/delete style) */
.submit-btn.danger-variant {
  background: var(--Secondary-Red-100, #F4003A);
  box-shadow: 0px 1px 2px 0px rgba(124, 0, 31, 0.50), 0px 0px 0px 1px #C80031;
}

.submit-btn.danger-variant.enabled:hover {
  background: #E00034;
}

.submit-btn.danger-variant.disabled {
  background: #F6F8FA;
  color: #A4ABB8;
  box-shadow: none;
}

/* Primary variant (blue/confirm style) */
.submit-btn.primary-variant {
  background: #2563EB;
  box-shadow: 0px 1px 2px 0px rgba(30, 64, 175, 0.50), 0px 0px 0px 1px #1E40AF;
}

.submit-btn.primary-variant.enabled:hover {
  background: #1D4ED8;
}

.submit-btn.primary-variant.disabled {
  background: #F6F8FA;
  color: #A4ABB8;
  box-shadow: none;
}
</style>
